import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AppLogo from '@/components/common/AppLogo';
import { useTheme } from '@/context/ThemeContext';
import guestBgImg from '@/assets/guest-bg.png';
import { useTranslation } from '@/hooks/useTranslation';
import { NAMESPACES } from '@/i18n/namespaces';
import Modal from '@/components/common/Modal';
import AuthModalContent from '@/components/auth/AuthModalContent';

const GuestWelcome: React.FC = () => {
  const { theme } = useTheme();
  const appName = theme === 'zap-odeon' ? 'zapOdeon' : 'zapDudes';
  const { t } = useTranslation(NAMESPACES.WELCOME);
  const [showCreateAccountModal, setShowCreateAccountModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);

  const handleCreateAccountClick = () => {
    setShowCreateAccountModal(true);
  };

  const handleLoginClick = () => {
    setShowLoginModal(true);
  };

  // Set document title
  useEffect(() => {
    const title = `${t('title')} ${t('subtitle', { appName })} - ${t('tagline')}`;
    document.title = title;
  }, [t, appName]);

  return (
    <div className="h-dvh relative overflow-hidden bg-black">
      {/* Background Image with overlay */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat z-0 w-full h-full animate-zoom-bg" 
        style={{ backgroundImage: `url(${guestBgImg})` }}
      ></div>
      
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black bg-opacity-60 z-1"></div>
      
      {/* Content */}
      <div className="relative z-10 flex flex-col items-center justify-end h-dvh px-6 py-6 text-center">
        {/* Main content - now at bottom */}
        <div className="flex flex-col items-center max-w-[260px] w-full mx-auto animate-fade-in-up">
          {/* App Logo */}
          <div className="mb-[24px] animate-shine rounded-[18px]">
            <AppLogo size="medium" />
          </div>
          
          <h1 className="text-5xl font-bold text-white">{t('title')}</h1>
          <h1 className="text-5xl font-bold text-white mb-4">{t('subtitle', { appName })}</h1>
          
          <h2 className="max-w-md">
            {t('tagline')}
          </h2>
        </div>
        
        {/* Bottom section - centered links with max-width */}
        <div className="flex flex-col items-center mb-6 max-w-[250px] w-full mx-auto mt-[32px] animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
          <button 
            className="btn btn-primary mb-[12px] w-full"
            onClick={handleCreateAccountClick}
            type="button"
          >
            {t('buttons.createAccount')}
          </button>
          
          <button 
            className="btn btn-secondary w-full"
            onClick={handleLoginClick}
            type="button"
          >
            {t('buttons.haveAccount')}
          </button>
        </div>
        
        {/* Legal text */}
        <p className="text-center text-l2-12 mx-[58px] mb-[12px] animate-fade-in-up" style={{ color: 'var(--white-24)', animationDelay: '0.2s' }}>
          {t('legal.ageVerification')}{' '}
          <Link to="/privacy-policy" className="underline" style={{ color: 'var(--white-24)' }}>
            {t('legal.privacyPolicy')}
          </Link>{' '}
          {t('legal.andAccepted')}{' '}
          <Link to="/terms-of-use" className="underline" style={{ color: 'var(--white-24)' }}>
            {t('legal.termsOfUse')}
          </Link>.
        </p>
      </div>

      {/* Create Account Modal */}
      <Modal 
        isOpen={showCreateAccountModal} 
        onClose={() => setShowCreateAccountModal(false)}
        title={t('modal.createAccount.title')}
      >
        <AuthModalContent 
          onClose={() => setShowCreateAccountModal(false)}
          mode="signup" 
        />
      </Modal>

      {/* Login Modal */}
      <Modal 
        isOpen={showLoginModal} 
        onClose={() => setShowLoginModal(false)}
        title={t('modal.login.title')}
      >
        <AuthModalContent 
          onClose={() => setShowLoginModal(false)} 
          mode="login"
        />
      </Modal>
    </div>
  );
};

export default GuestWelcome; 